import React, { useState, useEffect } from 'react';
import { DollarSign, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { contactInfo } from '../mock';
import ContactForm from '../components/sections/ContactForm';
import { HTTP } from '../lib/httpStatus';
import PageMeta from '../components/sections/PageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = { name: '', email: '', phone: '', message: '', website: '' };

const Contact = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, formData);

      if (response.data.success) {
        toast.success('Message Sent Successfully!', {
          description: "We'll get back to you within 24 hours.",
        });
        setFormData(INITIAL_FORM);
      }
    } catch (error) {
      const status = error.response?.status;
      const description = status === HTTP.RATE_LIMITED
        ? 'Too many submissions. Please wait a few minutes and try again.'
        : (error.response?.data?.detail
            ?.toString?.()
            ?.replace?.(/\[.*?\]/g, '')
            ?.trim?.()
          || 'Please try again or call us directly at 919-685-7293.');
      toast.error('Submission Failed', { description });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <PageMeta
        title="Contact Bring-A-Nurse Senior Companion | Fuquay Varina, NC"
        description="Contact Bring-A-Nurse to schedule a senior nurse companion for a doctor appointment or healthcare visit in the Raleigh, NC area. Serving Fuquay Varina, Holly Springs, Apex, and Cary."
        path="/contact"
      />
      <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8 pb-0">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Ready to experience compassionate, professional nursing support? Contact us today to schedule your consultation.
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-br from-blue-50 to-white px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 mb-12">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Schedule Your Consultation Here
              </h2>
              <p className="text-lg text-gray-600 mb-6 text-center">
                We&apos;re here to answer your questions and discuss how we can support you or your loved one.
              </p>
              <ContactForm
                formData={formData}
                isSubmitting={isSubmitting}
                onChange={handleChange}
                onSubmit={handleSubmit}
              />
            </CardContent>
          </Card>

          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">WHAT TO EXPECT</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 rounded-full p-3 flex-shrink-0">
                      <DollarSign className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Pricing</h3>
                      <p className="text-gray-700 text-lg font-semibold">
                        $50 Client Intake & First Consultation Fee
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 rounded-full p-3 flex-shrink-0">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Service Areas</h3>
                      <p className="text-gray-700">{contactInfo.serviceAreas}</p>
                      <p className="text-sm text-gray-500 mt-1">{contactInfo.serviceAreaNote}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
